import { FC, useMemo } from 'react'
import { useFormikContext } from 'formik'
import Box from '@mui/material/Box'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import ClearIcon from '@mui/icons-material/Clear'
import { Group } from '@/api'
import { getFieldTestId } from '@/utils'
import useTranslate from '@/hooks/use-translate'
import DialogUploadValues from '@/types/dialogUploadValues'
import { NfField } from '../primitives/NfField'
import { nfSelectMenuProps } from '../nfFieldSx'
import {
  getDefaultOrgRootGroupId,
  getWizardAgencySelectOptions,
} from '../utils/wizardGroupConfigurations'
import { WizardAgencyOptionLabel } from './WizardAgencyOptionLabel'

export interface AgencyFieldProps {
  myGroups?: Group[]
  onManualGroupChange?: () => void
}

/**
 * Agence / équipe / organisation. Le pré-remplissage est dans useWizardDefaults.
 * La croix ramène à l’org racine (pas d’état vide métier) et compte comme un choix.
 */
export const AgencyField: FC<AgencyFieldProps> = ({
  myGroups,
  onManualGroupChange,
}) => {
  const formik = useFormikContext<DialogUploadValues>()
  const { t: tUpload } = useTranslate('upload')
  const selectOptions = useMemo(
    () => getWizardAgencySelectOptions(myGroups ?? []),
    [myGroups]
  )
  const defaultOrgRootGid = useMemo(
    () => getDefaultOrgRootGroupId(myGroups),
    [myGroups]
  )

  if (!myGroups) return null

  const noGroupsOnAccount = myGroups.length === 0
  const selectedGroupId = formik.values.group
  const selectedGroup =
    selectOptions.find((g) => g.gid === selectedGroupId) ??
    myGroups.find((g) => g.gid === selectedGroupId)

  const showClear = Boolean(
    defaultOrgRootGid &&
      selectedGroupId &&
      selectedGroupId !== defaultOrgRootGid
  )

  const applyGroupCascade = (group: string) => {
    onManualGroupChange?.()
    formik.setValues({
      ...formik.values,
      group,
      exportConfiguration: '',
      workflow: '',
      clientOrganizationId: '',
    })
  }

  return (
    <NfField
      label={tUpload('wizardAgencyLabel')}
      tooltip={tUpload('wizardAgencyHint')}
    >
      <Box sx={{ position: 'relative' }}>
        <Select
          variant="outlined"
          displayEmpty
          fullWidth
          disabled={noGroupsOnAccount || selectOptions.length === 0}
          inputProps={{ ['data-testid']: 'group-input' }}
          renderValue={() => {
            if (!selectedGroup) {
              return tUpload('wizardNoAgencyOption')
            }
            return (
              <WizardAgencyOptionLabel group={selectedGroup} compact />
            )
          }}
          MenuProps={nfSelectMenuProps}
          sx={
            showClear
              ? {
                  '& .MuiSelect-select.MuiSelect-outlined': {
                    paddingRight: '56px',
                  },
                }
              : undefined
          }
          value={selectedGroupId}
          onChange={(event) => {
            applyGroupCascade(event.target.value as string)
          }}
        >
          {selectOptions.map((group, index) => (
            <MenuItem
              key={group.gid}
              value={group.gid}
              {...getFieldTestId(`group-option-${index}`)}
            >
              <WizardAgencyOptionLabel
                group={group}
                showOrganizationBadge
              />
            </MenuItem>
          ))}
        </Select>
        {showClear ? (
          <IconButton
            size="small"
            aria-label={tUpload('wizardAgencyClearToOrganization')}
            onClick={() => applyGroupCascade(defaultOrgRootGid!)}
            data-testid="group-clear-icon"
            sx={(theme) => ({
              position: 'absolute',
              right: 28,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: theme.palette.background.paper,
              '&:hover': { bgcolor: theme.palette.action.hover },
            })}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
    </NfField>
  )
}

export default AgencyField
